import { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "configureStore";
import {
  fetchCustomerOrdersList,
  setDestroy,
} from "features/customers-orders/customer-orders.slice";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { EditIcon } from "@shopify/polaris-icons";
import { Column } from "types/Datatable";
import { OrderProductLines } from "features/orders/order-product-lines";
import { buildOrderPhoneColumns } from "features/orders/order-phone-columns";
import useIsPhone from "features/files/use-is-phone";

function CustomerOrdersContainer() {
  const navigate = useNavigate();
  const { collection, isLoading } = useSelector(
    (state: RootState) => state.customerOrdersReducer
  );
  const { role } = useSelector((state: RootState) => state.authReducer);

  const hasUserSignaturePermission =
    role?.permissionsgroups?.includes("Signature");

  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId, customerId } = useParams();
  const isPhone = useIsPhone();

  const fetchData = useCallback(
    (companyId: string) => {
      if (companyId && customerId) {
        dispatch(fetchCustomerOrdersList(companyId, customerId));
      }
    },
    [dispatch, customerId]
  );

  const onRowClick = ({ id }: { id: string }) => {
    // navigate to the order view page
    navigate(`${id}`);
  };

  const onEditClick = ({
    id,
    isEditButtonDisabled,
  }: {
    id: string;
    isEditButtonDisabled: boolean;
  }) => {
    if (!isEditButtonDisabled) {
      navigate(`/app/${companyId}/customers/${customerId}/orders/${id}/edit`);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchData(companyId);
    }

    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, fetchData, customerId, dispatch]);

  if (!companyId || !collection) {
    return null;
  }

  const ordersCollection =
    collection.map((item: any) => ({
      id: item.id,
      date: item.date,
      totalPrice: item.totalPrice,
      responsibleUser: item.responsibleUser?.name || "",
      companyId: item.companyId,
      customerId: item.customerId,
      products: item.products,
      signature: item.files.length ? item.files[0].signStatus : "not_initiate",
      tone: item.files.length
        ? item.files[0].signStatus === "pending"
          ? "attention"
          : item.files[0].signStatus === "signed"
          ? "success"
          : "info"
        : "info",
      isEditButtonDisabled: item.files.length
        ? item.files[0].signStatus === "signed" && !hasUserSignaturePermission
          ? true
          : false
        : false,
      number: item._number,
      internal_order_number: item.number,
    })) || [];

  const baseColumns: Column[] = [
    {
      key: "internal_order_number",
      name: t("internal_order_number"),
      onClick: onRowClick,
    },
    {
      key: "date",
      name: t("date"),
      type: "clickabledate",
      onClick: onRowClick,
    },
    {
      key: "number",
      name: t("number"),
      onClick: onRowClick,
    },
    {
      key: "products",
      name: t("products"),
      type: "custom",
      render: (row: any) => <OrderProductLines products={row.products} />,
    },
    {
      key: "totalPrice",
      name: t("totalPrice"),
      type: "currency",
      onClick: onRowClick,
    },
    {
      key: "signature",
      name: t("signature"),
      translationRequired: true,
      type: "badge",
      onClick: onRowClick,
    },
    {
      key: "responsibleUser",
      name: t("user"),
      onClick: onRowClick,
    },
    {
      key: "edit",
      name: t("edit"),
      onClick: onEditClick,
      type: "actionBtn",
      Icon: EditIcon,
    },
  ];
  const columns = isPhone
    ? buildOrderPhoneColumns({ showCustomer: false })
    : baseColumns.filter((column) => column.key !== "status");

  // The customer page around this list already has the title and the
  // "create order" button.
  return isLoading ? (
    <DataTableSkeleton
      rows={10}
      columns={columns.length}
      iconColumnIndexes={[7]}
    />
  ) : (
    <DataTable
      collection={ordersCollection}
      columns={columns}
      // A phone's card has no cells to tap, so the whole card opens the order.
      rowUrl={
        isPhone
          ? (order) => `/app/${companyId}/customers/${customerId}/orders/${order.id}`
          : undefined
      }
    />
  );
}

export default CustomerOrdersContainer;
